import React, { useState, useEffect } from 'react';
import ApiService from '../../../services/ApiService';

const FlagsSection = ({ userId, onMarkAsRead }) => {
  const [data, setData] = useState({ counts: { pending: 0, accepted: 0, rejected: 0, boycotts: 0 }, items: [], notifications: [] });
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('recent');
  const [showSortMessage, setShowSortMessage] = useState(false);

  const toggleSort = () => {
    const newOrder = sortOrder === 'recent' ? 'oldest' : 'recent';
    setSortOrder(newOrder);
    setShowSortMessage(true);
    setTimeout(() => setShowSortMessage(false), 1500);
  };

  useEffect(() => {
    fetchFlagSummary();
  }, [userId]);

  const fetchFlagSummary = async () => {
    setLoading(true);
    try {
      const response = await ApiService.get('moderation/flags/summary');
      if (response.data?.success) {
        setData(response.data.data);
        // Mark flags section as read
        if (onMarkAsRead) onMarkAsRead('flags');
      }
    } catch (error) {
      console.error('Error fetching flag summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status, type) => {
    // Boycott-specific statuses
    if (type === 'boycott') {
      const boycottStyles = {
        pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
        under_review: { bg: '#dbeafe', color: '#1e40af', label: 'Under Review' },
        approved: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
        rejected: { bg: '#fee2e2', color: '#dc2626', label: 'Rejected' },
      };
      const config = boycottStyles[status] || boycottStyles.pending;
      return (
        <span style={{
          padding: '3px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '500',
          background: config.bg,
          color: config.color
        }}>
          {config.label}
        </span>
      );
    }

    // Regular flag statuses
    const styles = {
      pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending Review' },
      reviewed: { bg: '#dcfce7', color: '#166534', label: 'Reviewed' },
      auto_hidden: { bg: '#fee2e2', color: '#dc2626', label: 'Auto-Hidden' },
      rejected: { bg: '#e5e7eb', color: '#374151', label: 'No Violation Found' },
    };
    const config = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '500',
        background: config.bg,
        color: config.color
      }}>
        {config.label}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    if (type === 'flag_rejected') return 'bi-flag-fill';
    if (type === 'boycott') return 'bi-slash-circle';
    const icons = {
      review: 'bi-chat-square-quote',
      article: 'bi-journal-text',
      event: 'bi-calendar-event',
      user: 'bi-person',
      kudos: 'bi-heart',
    };
    return icons[type] || 'bi-file-text';
  };

  const getTypeLabel = (item) => {
    if (item.type === 'flag_rejected') return 'Your Flag Report';
    if (item.type === 'boycott') return 'Boycott Report';
    return item.contentType?.charAt(0).toUpperCase() + item.contentType?.slice(1) || 'Content';
  };

  const formatBoycottReason = (reason) => {
    const reasonLabels = {
      'safety_concerns': 'Safety concerns',
      'fraudulent': 'Fraudulent event',
      'inappropriate_content': 'Inappropriate content',
      'repeated_issues': 'Repeated issues',
      'organizer_misconduct': 'Organizer misconduct',
      'discrimination': 'Discrimination',
      'other': 'Other',
    };
    return reasonLabels[reason] || reason;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const { counts, items } = data;

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Flags</h1>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
          Track your flagged content, flag reports, and boycott submissions
        </p>
      </div>

      {/* Tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#92400e' }}>{counts.pending}</div>
          <div style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: '500' }}>Pending</div>
          <div style={{ fontSize: '0.7rem', color: '#a16207', marginTop: '2px' }}>Content under review</div>
        </div>

        <div style={{
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#166534' }}>{counts.accepted}</div>
          <div style={{ fontSize: '0.8rem', color: '#166534', fontWeight: '500' }}>Accepted</div>
          <div style={{ fontSize: '0.7rem', color: '#15803d', marginTop: '2px' }}>Flags upheld</div>
        </div>

        <div style={{
          background: '#f9fafb',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#374151' }}>{counts.rejected}</div>
          <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '500' }}>Rejected</div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>No violation found</div>
        </div>

        <div style={{
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#b91c1c' }}>{counts.boycotts || 0}</div>
          <div style={{ fontSize: '0.8rem', color: '#b91c1c', fontWeight: '500' }}>Boycotts</div>
          <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '2px' }}>Your boycott reports</div>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        alignItems: 'flex-start'
      }}>
        <i className="bi bi-info-circle" style={{ color: '#3b82f6', marginTop: '2px' }}></i>
        <div style={{ fontSize: '0.85rem', color: '#1e40af' }}>
          <strong>How it works:</strong> When content is flagged, our team reviews it.
          "Pending" means under review. "Accepted" means action was taken.
          "Rejected" shows reports where no violation was found.
          "Boycotts" tracks your event boycott submissions.
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
          <i className="bi bi-shield-check" style={{ fontSize: '2.5rem', color: '#22c55e', display: 'block', marginBottom: '1rem' }}></i>
          <h3 style={{ margin: '0 0 0.5rem', color: '#374151' }}>All Clear</h3>
          <p style={{ margin: 0 }}>No flagged content, reports, or boycotts to show.</p>
        </div>
      ) : (
        <>
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
          {showSortMessage && (
            <span style={{ fontSize: '0.8rem', color: '#6B7280', animation: 'fadeIn 0.2s ease-out' }}>
              {sortOrder === 'recent' ? 'Most recent' : 'Oldest first'}
            </span>
          )}
          <button
            onClick={toggleSort}
            title={sortOrder === 'recent' ? 'Showing most recent' : 'Showing oldest first'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              color: '#9CA3AF', display: 'flex', alignItems: 'center', transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
          >
            <i className={`bi ${sortOrder === 'recent' ? 'bi-sort-down' : 'bi-sort-up'}`} style={{ fontSize: '1.1rem' }}></i>
          </button>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Type</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Content</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {[...items].sort((a, b) => {
                const dateA = new Date(a.createdAt);
                const dateB = new Date(b.createdAt);
                return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
              }).map((item, idx) => (
                <tr key={item.id || idx} style={{ borderBottom: idx < items.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i className={`bi ${getTypeIcon(item.type)}`} style={{
                        color: item.type === 'flag_rejected' ? '#6b7280' :
                               item.type === 'boycott' ? '#dc2626' : '#6366f1'
                      }}></i>
                      <span style={{ fontSize: '0.85rem' }}>{getTypeLabel(item)}</span>
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', maxWidth: '300px' }}>
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#374151'
                    }}>
                      {item.contentPreview || 'Content'}
                    </div>
                    {item.type === 'boycott' && item.reason && (
                      <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '2px' }}>
                        Reason: {formatBoycottReason(item.reason)}
                      </div>
                    )}
                    {item.type !== 'boycott' && item.message && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                        {item.message}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {getStatusBadge(item.status, item.type)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.85rem' }}>
                    {formatDate(item.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

export default FlagsSection;
